import { useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { HTMLParagraph } from '@/components/HTMLParagraph';
import { Paragraph } from '@/components/Paragraph';
import { useDeviceBreakpoints } from '@/hooks/useDeviceBreakpoints';
import { StyledFlexColumn } from '@/layout/FlexLayout';
import { getMainContentItineraryProduct, getRoomTypesAccommodationProduct } from '@/pages/Accommodation/helpers';
import { RoomUpgradeModal } from '@/pages/Accommodation/RoomUpgradeModal';
import {
  StyledSelectedAccommodationChoice,
  StyledAccommodationBlock,
  StyledUpgradeLabel,
  StyledFlexbox,
  StyledRow,
  StyledContentGrid,
  StyledExtras,
  StyledButtons,
  StyledMoreImagesButton,
  StyledStar,
  StyledTag,
} from '@/pages/Accommodation/styles';
import { StyledMainImage, StyledImageGrid, StyledImage } from '@/styles/images';
import { createSubtitle } from '@/utils/createSubtitleItineryProduct';
import { getIcon } from '@/utils/getIcon';

import { RoomImagesModal } from './RoomImagesModal';

import type { ItineraryProduct } from '@/types/products';

interface AccommodationProductProps {
  product: ItineraryProduct;
  openModal: (id: number) => void;
  closeModal: (id: number) => void;
  modalStates: { [key: string]: boolean };
}

export function AccommodationProduct({ product, openModal, modalStates, closeModal }: AccommodationProductProps) {
  const mainContent = useMemo(() => getMainContentItineraryProduct(product), [product]);
  const subtitle = useMemo(() => createSubtitle(product), [product]);
  const { isMobile } = useDeviceBreakpoints();

  const [isImagesModalOpened, setIsImagesModalOpened] = useState(false);

  const mainImage = useMemo(
    () => mainContent?.RichContent?.filter((content) => content.subType?.toLowerCase() === 'header')[0]?.path,
    [mainContent],
  );

  const remainingImages = useMemo(
    () => mainContent?.RichContent?.filter((content) => content.subType?.toLowerCase() !== 'header') || [],
    [mainContent],
  );

  const COUNT_IMAGES_TO_SHOW = 4;

  const roomTypes = useMemo(() => getRoomTypesAccommodationProduct(product), [product]);

  return (
    <>
      <StyledSelectedAccommodationChoice>
        <h3>{product.selected ? 'Selected accommodation' : 'Alternative Accommodation'}</h3>
      </StyledSelectedAccommodationChoice>

      <StyledAccommodationBlock key={product.id}>
        {!product.selected && (
          <StyledUpgradeLabel>
            <span className="top" />
            Upgrade
            <span />
          </StyledUpgradeLabel>
        )}
        <StyledFlexbox>
          <h3>{mainContent?.header}</h3>
          <StyledRow>
            <p>{subtitle}</p>
          </StyledRow>
        </StyledFlexbox>
        <StyledContentGrid>
          <StyledMainImage modal={false} src={mainImage} alt="Hotel header image" />
          <RoomImagesModal
            showModal={isImagesModalOpened}
            closeModal={() => {
              setIsImagesModalOpened(false);
            }}
            media={remainingImages}
          />
          <StyledMoreImagesButton
            onClick={() => {
              setIsImagesModalOpened(true);
            }}
          >
            View all photos
          </StyledMoreImagesButton>
          {/* Show 4 more images on bigger screens */}
          {!isMobile && (
            <StyledImageGrid>
              {!isMobile &&
                remainingImages &&
                remainingImages.length > 0 &&
                remainingImages
                  .slice(0, COUNT_IMAGES_TO_SHOW)
                  .map((content) => (
                    <StyledImage modal={false} key={content?.id} src={content.path} alt="Hotel image" />
                  ))}
            </StyledImageGrid>
          )}
          <StyledFlexColumn>
            {!product.selected && (
              <StyledTag>
                <StyledStar>{getIcon('star', 'xl')}</StyledStar>
                Accommodation upgrade
              </StyledTag>
            )}
            <HTMLParagraph dangerousHTML={mainContent?.paragraph ?? ''} />
          </StyledFlexColumn>
          <StyledFlexbox>
            <StyledExtras>
              {product.productFacilities && (
                <StyledFlexbox>
                  <Paragraph color="primary" size="md" weight={600}>
                    Facilities
                  </Paragraph>
                  {product.productFacilities.map((facility) => (
                    <p key={facility}>
                      {getIcon(facility)} &nbsp;
                      {facility}
                    </p>
                  ))}
                </StyledFlexbox>
              )}
              {roomTypes && (
                <StyledFlexbox>
                  <Paragraph color="primary" size="md" weight={600}>
                    Room types
                  </Paragraph>
                  {roomTypes.map((roomType) => (
                    <p key={roomType}>
                      {getIcon('accommodation')} &nbsp; 1 x {roomType}
                    </p>
                  ))}

                  <Paragraph color="primary" size="md" weight={600}>
                    Meals
                  </Paragraph>

                  <p key="meal1">{getIcon('restaurant')} &nbsp;Excl. breakfast</p>
                </StyledFlexbox>
              )}
              <StyledFlexbox />
            </StyledExtras>

            <StyledButtons>
              {product.selected && product.alternate && (
                <>
                  <Button variant="secondary" size="small" onClick={() => openModal(product.id)}>
                    View room upgrades
                  </Button>
                  <RoomUpgradeModal
                    showModal={modalStates[product.id] === true}
                    closeModal={() => closeModal(product.id)}
                    rooms={product.alternate}
                  />
                </>
              )}
              {!product.selected && (
                <Button variant="secondary" size="small">
                  Select + €{product.sellAmount} p.p.
                </Button>
              )}
            </StyledButtons>
          </StyledFlexbox>
        </StyledContentGrid>
      </StyledAccommodationBlock>
    </>
  );
}
